'use client';

/**
 * LegalModal — popup modal that renders the existing legal-page bodies
 * (Terms / Privacy / Medical Disclaimer / Refund / Cookies / SMS Terms)
 * inline so a misclick on a "Terms" link mid-funnel doesn't navigate
 * the buyer away from /checkout / /upsell / /start and lose form state.
 *
 * Track 3.3 (2026-05-13). Triggered by <LegalLink slug="..."> through
 * the LegalModalProvider context. Slug → page-path mapping lives in
 * LEGAL_PAGES below; the modal fetches the live page HTML and grafts
 * the `<main>` element's innerHTML into the modal body — single source
 * of truth, no content duplication, edits to /terms etc. flow through
 * the modal automatically.
 *
 * Accessibility:
 *   - `role="dialog" aria-modal="true" aria-label`
 *   - Esc closes
 *   - Backdrop click closes
 *   - Focus moves to the close button on open
 *   - Body scroll locked while open
 *
 * Fallback: if fetch/parse fails the modal renders an "Open in new
 * tab" link so the buyer can still reach the document.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export const LEGAL_PAGES = {
  terms: { title: 'Terms of Service', path: '/terms' },
  'privacy-policy': { title: 'Privacy Policy', path: '/privacy-policy' },
  'medical-disclaimer': { title: 'Medical Disclaimer', path: '/medical-disclaimer' },
  'refund-policy': { title: 'Refund Policy', path: '/refund-policy' },
  'cookie-policy': { title: 'Cookie Policy', path: '/cookie-policy' },
  'sms-terms': { title: 'SMS Terms', path: '/legal/sms-terms' },
} as const;

export type LegalSlug = keyof typeof LEGAL_PAGES;

const CONTENT_CACHE = new Map<LegalSlug, string>();

interface LegalModalProps {
  slug: LegalSlug | null;
  onClose: () => void;
}

export function LegalModal({ slug, onClose }: LegalModalProps) {
  // contentSlug pairs with content so a slug switch (e.g. terms →
  // privacy-policy) doesn't briefly render the OLD body in the new
  // modal — render gates on contentSlug === slug.
  const [content, setContent] = useState<string | null>(null);
  const [contentSlug, setContentSlug] = useState<LegalSlug | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [mounted, setMounted] = useState(false);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!slug) return;

    // Lock body scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Esc to close
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // Move focus to close button next paint
    requestAnimationFrame(() => {
      closeBtnRef.current?.focus();
    });

    // Load content. If we have a cache hit the loader is sync; otherwise
    // the fetch fires and the .then() updates state (and an in-flight
    // race against a slug switch is ignored via the `cancelled` flag).
    const def = LEGAL_PAGES[slug];
    const cached = CONTENT_CACHE.get(slug);
    let cancelled = false;
    if (cached) {
      // Synchronous setState in effect on cache hit. The lint rule
      // exists to prevent cascading-renders patterns; here we set
      // state once on slug change to a known-good value, no cascade.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setContent(cached);
      setContentSlug(slug);
      setError(null);
    } else {
      setError(null);
      fetch(def.path, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((html) => {
          if (cancelled) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const main = doc.querySelector('main');
          if (!main) throw new Error('No <main> in document');
          const inner = main.innerHTML;
          CONTENT_CACHE.set(slug, inner);
          setContent(inner);
          setContentSlug(slug);
        })
        .catch((e) => {
          if (!cancelled) setError((e as Error).message);
        });
    }

    return () => {
      cancelled = true;
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [slug, onClose]);

  if (!mounted || !slug) return null;
  const def = LEGAL_PAGES[slug];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={def.title}
    >
      <div
        className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — title + large X close */}
        <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-[#0f1e3d] truncate">
            {def.title}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-[#0f1e3d] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff9100] focus:ring-offset-2"
          >
            <X size={26} strokeWidth={2.5} aria-hidden />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 sm:py-6 text-sm sm:text-[15px] text-slate-700 leading-relaxed legal-modal-body">
          {error ? (
            <div className="text-center py-12 space-y-3">
              <p className="text-slate-600">
                We couldn&apos;t load this document right now.
              </p>
              <a
                href={def.path}
                target="_blank"
                rel="noopener"
                className="inline-block text-[#ff9100] hover:underline font-medium"
              >
                Open in a new tab →
              </a>
            </div>
          ) : !content || contentSlug !== slug ? (
            <p className="text-center text-slate-400 py-12">Loading…</p>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          )}
        </div>

        {/* Footer with secondary close (mobile-friendly) */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-200 flex items-center justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-[#ff9100] hover:bg-[#ff7f00] text-white text-sm font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
